import { isArray } from 'lodash-es'
import { objectEntries } from 'ts-extras'
import type { Entry } from 'type-fest'
import type { Nullable } from '~/types'

/**
 * Converts strings from snake case to camel case
 */
export const snakeToCamel = (str: string) =>
	str
		.split('-')
		.map(name => name.charAt(0).toUpperCase() + name.slice(1))
		.join(' ')

export function range(start: number, end?: number): number[] {
	if (end === undefined) {
		end = start
		start = 0
	}
	return new Array(Math.abs(end - start) + 1).fill(start).map((_, i) => _ + i * Math.sign(end as number))
}

export const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

export function includes<T extends string>(str: T | undefined, ...searches: T[]) {
	return str !== undefined && searches.some(s => str.includes(s))
}

export function objectMap<T extends Record<string, any>>(obj: T, func: (value: T[typeof key], key: keyof T) => void) {
	return objectEntries(obj).reduce(
		(a, [key, value]) => ({
			...a,
			[key]: func(value as any, key as keyof T)
		}),
		{} as Entry<T>
	)
}

export function hasDecimals(n: number) {
	return typeof n === 'number' && n % 1 !== 0
}

export function parseNumber(n: Nullable<string | number>): number {
	if (typeof n === 'number' && !isNaN(n)) return n
	const result = Number.parseFloat((n as any) || '0')
	return result || 0
}

export function isNonEmptyArray(arr: any) {
	return isArray(arr) && arr.length > 0
}

export interface Duration {
	total: number
	day: string | number
	hour: string | number
	minute: string | number
	second: string | number
}

export function diffDates(a: Date, b: Date): Duration {
	function pad(n: number) {
		n = Math.floor(n)
		return n < 10 ? `0${n}` : n
	}
	const SECONDS_PER_HOUR = 60 * 60
	const SECONDS_PER_DAY = SECONDS_PER_HOUR * 24

	const remaining = Math.max(b.valueOf() - a.valueOf(), 0) / 1000
	return {
		total: remaining,
		day: pad(remaining / SECONDS_PER_DAY),
		hour: pad((remaining % SECONDS_PER_DAY) / SECONDS_PER_HOUR),
		minute: pad((remaining % SECONDS_PER_HOUR) / 60),
		second: pad(remaining % 60)
	}
}
